import { useState, useEffect } from "react";
import { C, MUSCLE_GROUPS, MUSCLE_SUBGROUPS } from "../constants";
import { Modal, Btn, iStyle, lblSt } from "./ui";

const SubgroupMultiSelect = ({ label, value = [], onChange, exclude = [] }) => {
  const excludeSet = new Set(exclude);
  const toggle = (sub) => {
    if (value.includes(sub)) onChange(value.filter(s => s !== sub));
    else onChange([...value, sub]);
  };
  return (
    <div style={{ marginBottom: 12 }}>
      <label style={lblSt}>{label}</label>
      <div style={{ background: C.surface, border: `1px solid ${C.border}`, borderRadius: 8, padding: 8, maxHeight: 180, overflowY: "auto", display: "flex", flexDirection: "column", gap: 8 }}>
        {Object.entries(MUSCLE_SUBGROUPS).map(([group, subs]) => {
          const visible = subs.filter(s => !excludeSet.has(s));
          if (visible.length === 0) return null;
          return (
            <div key={group}>
              <div style={{ fontSize: 9, color: C.muted, textTransform: "uppercase", letterSpacing: "0.06em", fontWeight: 700, marginBottom: 4 }}>{group}</div>
              <div style={{ display: "flex", flexWrap: "wrap", gap: 4 }}>
                {visible.map(sub => {
                  const active = value.includes(sub);
                  return (
                    <button key={sub} type="button" onClick={() => toggle(sub)} style={{
                      padding: "4px 10px", borderRadius: 5, border: "none", cursor: "pointer",
                      fontSize: 11, fontWeight: 600, fontFamily: "inherit",
                      background: active ? C.accent : C.card,
                      color: active ? "#1e1d1b" : C.muted,
                    }}>{sub}</button>
                  );
                })}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

export const CustomExerciseModal = ({ initial, lockMetadata = false, onSave, onClose, onDelete, deleteLabel = "Delete", advancedMode = false }) => {
  const [name,        setName]        = useState(initial?.name        || "");
  const [muscleGroup, setMuscleGroup] = useState(initial?.muscleGroup || "Chest");
  const [subgroup,    setSubgroup]    = useState(initial?.p           || MUSCLE_SUBGROUPS["Chest"][0]);
  const [type,        setType]        = useState(initial?.type        || "machine");
  const [secondary,   setSecondary]   = useState(initial?.s || []);
  const [stabilisers, setStabilisers] = useState(initial?.t || []);
  const [useCustomIncrement, setUseCustomIncrement] = useState(!!initial?.useCustomIncrement);
  const [increment,   setIncrement]   = useState(initial?.increment ?? 2.5);
  const [progressionDirection, setProgressionDirection] = useState(initial?.progressionDirection || "up");

  useEffect(() => {
    if (!initial) return;
    setName(initial.name || "");
    setMuscleGroup(initial.muscleGroup || "Chest");
    setSubgroup(initial.p || MUSCLE_SUBGROUPS[initial.muscleGroup || "Chest"][0]);
    setType(initial.type || "machine");
    setSecondary(initial.s || []);
    setStabilisers(initial.t || []);
    setUseCustomIncrement(!!initial.useCustomIncrement);
    setIncrement(initial.increment ?? 2.5);
    setProgressionDirection(initial.progressionDirection || "up");
  }, [initial]);

  const onChangeGroup = g => {
    setMuscleGroup(g);
    if (!MUSCLE_SUBGROUPS[g].includes(subgroup)) {
      const next = MUSCLE_SUBGROUPS[g][0];
      setSubgroup(next);
      setSecondary(prev => prev.filter(s => s !== next));
      setStabilisers(prev => prev.filter(s => s !== next));
    }
  };

  const onChangeSubgroup = sub => {
    setSubgroup(sub);
    setSecondary(prev => prev.filter(s => s !== sub));
    setStabilisers(prev => prev.filter(s => s !== sub));
  };

  const handleSave = () => {
    if (!name.trim()) return;
    const payload = {
      name: name.trim(),
      muscleGroup,
      type,
      p: subgroup,
      s: secondary,
      t: stabilisers,
      useCustomIncrement,
      progressionDirection,
    };
    if (useCustomIncrement) payload.increment = +increment || 0;
    onSave(payload);
    onClose();
  };

  return (
    <Modal onClose={onClose} title={initial ? "Edit Exercise" : "Add Custom Exercise"}>
      {lockMetadata ? (
        <div style={{ background: C.surface, border: `1px solid ${C.border}`, borderRadius: 10, padding: "12px 14px", marginBottom: 16 }}>
          <div style={{ fontWeight: 700, fontSize: 14, color: C.text, marginBottom: 4 }}>{name}</div>
          <div style={{ display: "flex", gap: 6, alignItems: "center", color: C.muted, fontSize: 12, flexWrap: "wrap" }}>
            <span>{muscleGroup}</span>
            <span>·</span>
            <span>{subgroup}</span>
            <span>·</span>
            <span style={{ textTransform: "capitalize" }}>{type}</span>
          </div>
        </div>
      ) : (
        <>
          <div style={{ marginBottom: 12 }}>
            <label style={lblSt}>Name</label>
            <input value={name} onChange={e => setName(e.target.value)} style={iStyle} placeholder="e.g. Cable Pull-through" />
          </div>
          <div style={{ marginBottom: 12 }}>
            <label style={lblSt}>Muscle Group</label>
            <select value={muscleGroup} onChange={e => onChangeGroup(e.target.value)} style={iStyle}>
              {MUSCLE_GROUPS.map(g => <option key={g}>{g}</option>)}
            </select>
          </div>
          <div style={{ marginBottom: 12 }}>
            <label style={lblSt}>{advancedMode ? "Primary Muscle" : "Subgroup"}</label>
            <select value={subgroup} onChange={e => onChangeSubgroup(e.target.value)} style={iStyle}>
              {(MUSCLE_SUBGROUPS[muscleGroup] || []).map(s => <option key={s}>{s}</option>)}
            </select>
          </div>
          <div style={{ marginBottom: 12 }}>
            <label style={lblSt}>Equipment</label>
            <select value={type} onChange={e => setType(e.target.value)} style={iStyle}>
              <option value="barbell">Barbell</option>
              <option value="dumbbell">Dumbbell</option>
              <option value="machine">Machine / Cable / Bodyweight</option>
            </select>
          </div>
          {advancedMode && (
            <>
              <SubgroupMultiSelect label="Secondary Muscles" value={secondary}   onChange={setSecondary}   exclude={[subgroup]} />
              <SubgroupMultiSelect label="Stabilisers"       value={stabilisers} onChange={setStabilisers} exclude={[subgroup]} />
            </>
          )}
          <div style={{ marginBottom: 12 }}>
            <label style={lblSt}>Progression Direction</label>
            <div style={{ display: "flex", gap: 6, background: C.surface, border: `1px solid ${C.border}`, borderRadius: 8, padding: 4 }}>
              {[
                { id: "up",   label: "Increase weight" },
                { id: "down", label: "Decrease assistance" },
              ].map(opt => (
                <button key={opt.id} type="button" onClick={() => setProgressionDirection(opt.id)}
                  style={{
                    flex: 1, padding: "7px 10px", borderRadius: 6, border: "none", cursor: "pointer",
                    background: progressionDirection === opt.id ? C.accent : "transparent",
                    color: progressionDirection === opt.id ? "#1e1d1b" : C.muted,
                    fontWeight: 600, fontSize: 12, fontFamily: "inherit",
                  }}>
                  {opt.label}
                </button>
              ))}
            </div>
          </div>
        </>
      )}
      <div style={{ marginBottom: 18 }}>
        <label style={lblSt}>Increment</label>
        <div style={{ display: "flex", gap: 6, background: C.surface, border: `1px solid ${C.border}`, borderRadius: 8, padding: 4, marginBottom: useCustomIncrement ? 8 : 0 }}>
          {[
            { id: false, label: "Automatic" },
            { id: true,  label: "Custom" },
          ].map(opt => (
            <button key={String(opt.id)} type="button" onClick={() => setUseCustomIncrement(opt.id)}
              style={{
                flex: 1, padding: "7px 10px", borderRadius: 6, border: "none", cursor: "pointer",
                background: useCustomIncrement === opt.id ? C.accent : "transparent",
                color: useCustomIncrement === opt.id ? "#1e1d1b" : C.muted,
                fontWeight: 600, fontSize: 12, fontFamily: "inherit",
              }}>
              {opt.label}
            </button>
          ))}
        </div>
        {useCustomIncrement && (
          <input
            type="number" step="0.5" min="0"
            value={increment}
            onChange={e => setIncrement(e.target.value)}
            style={iStyle}
            placeholder="e.g. 1.25"
          />
        )}
      </div>
      <div style={{ display: "flex", gap: 8 }}>
        <Btn variant="primary" onClick={handleSave} style={{ flex: 1 }}>
          {initial ? "Save Changes" : "Save Exercise"}
        </Btn>
        {initial && onDelete && (
          <Btn variant="danger" onClick={() => { onDelete(initial); onClose(); }} style={{ flex: 1 }}>
            {deleteLabel}
          </Btn>
        )}
      </div>
    </Modal>
  );
};
